/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from "react";
import {
  Sparkles, Plus, Upload, Search, Filter, Users,
  TrendingUp, Mail, Calendar, Target, ChevronRight,
  Activity, Zap, ArrowRight, MoreVertical, RefreshCw,
  AlertCircle, Globe, Instagram, MessageSquare, Check,
  Settings, Link, Save
} from "lucide-react";
import { GrowthProvider, useGrowth } from "../../../context/GrowthContext";
import { ManagerAgent } from "../../../services/growth/agents/ManagerAgent";
import { ResearchAgent } from "../../../services/growth/agents/ResearchAgent";
import { GrowthService, PIPELINE_STAGES } from "../../../services/growth/GrowthService";
import LeadDetailPanel from "./LeadDetailPanel";
import AddLeadModal from "./AddLeadModal";
import ImportModal from "./ImportModal";
import ScraperPanel from "./ScraperPanel";

// ─── Colors ──────────────────────────────────────────────────────────────────

const STAGE_STYLES = {
  new:            { dot: "bg-gray-400",    badge: "bg-gray-100 text-gray-600" },
  researched:     { dot: "bg-blue-400",    badge: "bg-blue-50 text-blue-700" },
  outreach_sent:  { dot: "bg-yellow-400",  badge: "bg-yellow-50 text-yellow-700" },
  replied:        { dot: "bg-purple-400",  badge: "bg-purple-50 text-purple-700" },
  demo_scheduled: { dot: "bg-green-400",   badge: "bg-green-50 text-green-700" },
  converted:      { dot: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700" },
  not_interested: { dot: "bg-red-400",     badge: "bg-red-50 text-red-600" },
};

// ─── Stat Card ───────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent || "bg-gray-50"}`}>
        <Icon className="w-5 h-5 text-gray-700" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900 leading-none">{value ?? "—"}</p>
        <p className="text-xs text-gray-500 mt-1">{label}</p>
        {sub && <p className="text-xs text-gray-400">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Lead Row ────────────────────────────────────────────────────────────────

function LeadRow({ lead, onClick }) {
  const style = STAGE_STYLES[lead.status] || STAGE_STYLES.new;
  const stage = PIPELINE_STAGES.find((s) => s.id === lead.status);

  return (
    <div
      onClick={onClick}
      className="flex items-center gap-4 px-4 py-3.5 hover:bg-gray-50 cursor-pointer transition rounded-xl group"
    >
      <div className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900 truncate">{lead.name}</p>
          {lead.score && (
            <span className="text-xs text-gray-400 font-medium">{lead.score}/10</span>
          )}
        </div>
        <p className="text-xs text-gray-400 truncate">{lead.eventName} · {lead.city}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.badge}`}>
          {stage?.label || lead.status}
        </span>
        {lead.research && <Sparkles className="w-3.5 h-3.5 text-blue-400" />}
        {lead.outreach && <Mail className="w-3.5 h-3.5 text-green-400" />}
        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition" />
      </div>
    </div>
  );
}

// ─── Pipeline Kanban ─────────────────────────────────────────────────────────

function PipelineCard({ lead, onClick }) {
  const style = STAGE_STYLES[lead.status] || STAGE_STYLES.new;
  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-100 rounded-xl p-3 cursor-pointer hover:shadow-md transition group"
    >
      <p className="text-sm font-medium text-gray-900 truncate">{lead.name}</p>
      {lead.eventName && <p className="text-xs text-gray-400 truncate mt-0.5">{lead.eventName}</p>}
      <div className="flex items-center gap-1.5 mt-2">
        {lead.city && <span className="text-xs text-gray-400">{lead.city}</span>}
        {lead.score && <span className="text-xs text-gray-300">· {lead.score}/10</span>}
      </div>
    </div>
  );
}

function PipelineView({ leads, onLeadClick }) {
  return (
    <div className="flex gap-4 overflow-x-auto pb-4 min-h-[60vh]">
      {PIPELINE_STAGES.map((stage) => {
        const stageLeads = leads.filter((l) => l.status === stage.id);
        const style = STAGE_STYLES[stage.id];
        return (
          <div key={stage.id} className="shrink-0 w-56">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-2 h-2 rounded-full ${style.dot}`} />
              <span className="text-xs font-semibold text-gray-700">{stage.label}</span>
              <span className="ml-auto text-xs text-gray-400">{stageLeads.length}</span>
            </div>
            <div className="space-y-2">
              {stageLeads.map((lead) => (
                <PipelineCard key={lead.id} lead={lead} onClick={() => onLeadClick(lead)} />
              ))}
              {stageLeads.length === 0 && (
                <div className="border-2 border-dashed border-gray-100 rounded-xl h-20 flex items-center justify-center">
                  <span className="text-xs text-gray-300">Empty</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Batch Research Runner ────────────────────────────────────────────────────

function BatchResearchBar({ leads, onDone }) {
  const { setAgentRunning, refreshAfterAgent } = useGrowth();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);

  const unresearched = leads.filter((l) => !l.research && l.status === "new");

  const handleRun = async () => {
    if (!unresearched.length) return;
    setRunning(true);
    setAgentRunning("batch_research", true);
    try {
      await ResearchAgent.batchResearch(unresearched, (done, total) => {
        setProgress({ done, total });
        const lead = unresearched[done - 1];
        if (lead) {
          // We'll refresh fully via onDone
        }
      });
      onDone();
    } catch (err) {
      console.error(err);
    } finally {
      setRunning(false);
      setAgentRunning("batch_research", false);
      setProgress(null);
    }
  };

  if (!unresearched.length) return null;

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl">
      <Sparkles className="w-4 h-4 text-blue-500 shrink-0" />
      <p className="text-sm text-blue-800 flex-1">
        {unresearched.length} unresearched leads ready.
      </p>
      {progress ? (
        <span className="text-sm text-blue-600 font-medium">
          {progress.done}/{progress.total}
        </span>
      ) : (
        <button
          onClick={handleRun}
          disabled={running}
          className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
        >
          Research All
        </button>
      )}
    </div>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────────

function OverviewView({ leads, metrics, onLeadClick, onReload }) {
  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);

  const topLeads = useMemo(
    () => leads.filter((l) => l.score >= 7).slice(0, 5),
    [leads]
  );
  const followUpQueue = useMemo(
    () => leads.filter((l) => {
      if (l.status !== "outreach_sent") return false;
      const ts = l.lastContactedAt?.seconds ? l.lastContactedAt.seconds * 1000 : null;
      if (!ts) return false;
      return (Date.now() - ts) / (1000 * 60 * 60 * 24) >= 5;
    }),
    [leads]
  );

  const [agentRunning, setAgentRunning] = useState(false);
  const [agentResult, setAgentResult] = useState(null);

  const loadHealth = async () => {
    if (!metrics || loadingHealth) return;
    setLoadingHealth(true);
    try {
      const h = await ManagerAgent.analyzeHealth(metrics);
      setHealth(h);
    } catch {}
    setLoadingHealth(false);
  };

  const handleRunAgent = async () => {
    setAgentRunning(true);
    setAgentResult(null);
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const call = httpsCallable(getFunctions(), "triggerOutreachNow");
      const res = await call({});
      setAgentResult(res.data);
      onReload && onReload();
    } catch (err) {
      setAgentResult({ error: err.message });
    } finally {
      setAgentRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Leads" value={metrics?.total} icon={Users} accent="bg-gray-50" />
        <StatCard label="Outreach Sent" value={metrics?.withOutreach} icon={Mail} accent="bg-blue-50" />
        <StatCard label="Demos Booked" value={metrics?.demos} icon={Calendar} accent="bg-green-50" />
        <StatCard label="Response Rate" value={metrics?.responseRate != null ? `${metrics.responseRate}%` : "—"} icon={TrendingUp} accent="bg-purple-50" />
      </div>

      {/* AI Health */}
      <div className="bg-gray-900 text-white rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium">Manager Agent — Pipeline Health</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRunAgent}
              disabled={agentRunning}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-900 text-xs font-semibold rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
            >
              {agentRunning ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
              {agentRunning ? "Agent running..." : "Run Agent Now"}
            </button>
            <button
              onClick={loadHealth}
              disabled={loadingHealth}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white text-xs rounded-lg hover:bg-white/20 transition disabled:opacity-50"
            >
                {loadingHealth ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
              {loadingHealth ? "Analyzing..." : "Analyze"}
            </button>
          </div>
        </div>

        {agentResult && !agentResult.error && (
          <div className="mb-3 flex items-center gap-4 text-sm">
            <span className="text-green-400 font-semibold">✓ Agent complete</span>
            <span className="text-gray-400">{agentResult.sent} sent · {agentResult.failed} failed</span>
            <span className="text-gray-500 text-xs">Email: {agentResult.channels?.email || 0} · Form: {agentResult.channels?.contact_form || 0}</span>
          </div>
        )}
        {agentResult?.error && (
          <p className="mb-3 text-xs text-red-400">{agentResult.error}</p>
        )}

        {health ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="text-3xl font-bold">{health.healthScore}<span className="text-lg text-gray-400">/10</span></div>
              <p className="text-sm text-gray-300">{health.topPriority}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {health.nextSteps?.map((step, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-gray-400">
                  <ArrowRight className="w-3 h-3 text-gray-500 mt-0.5 shrink-0" />
                  {step}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Click Analyze to get AI insights on your pipeline.</p>
        )}
      </div>

      {/* Follow-up queue */}
      {followUpQueue.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
            Follow-Up Queue ({followUpQueue.length})
          </p>
          <div className="space-y-1">
            {followUpQueue.map((lead) => (
              <LeadRow key={lead.id} lead={lead} onClick={() => onLeadClick(lead)} />
            ))}
          </div>
        </div>
      )}

      {/* Top leads */}
      {topLeads.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
            High-Score Leads
          </p>
          <div className="space-y-1">
            {topLeads.map((lead) => (
              <LeadRow key={lead.id} lead={lead} onClick={() => onLeadClick(lead)} />
            ))}
          </div>
        </div>
      )}

      {leads.length === 0 && (
        <div className="text-center py-16">
          <Target className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No leads yet.</p>
          <p className="text-sm text-gray-400 mt-1">Add your first lead or import a CSV to get started.</p>
        </div>
      )}
    </div>
  );
}

// ─── Leads View ──────────────────────────────────────────────────────────────

function LeadsView({ leads, onLeadClick, onReload }) {
  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState("all");

  const filtered = useMemo(() => {
    let list = leads;
    if (filterStage !== "all") list = list.filter((l) => l.status === filterStage);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (l) =>
          l.name?.toLowerCase().includes(q) ||
          l.eventName?.toLowerCase().includes(q) ||
          l.city?.toLowerCase().includes(q) ||
          l.email?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [leads, search, filterStage]);

  return (
    <div className="space-y-4">
      <BatchResearchBar leads={leads} onDone={onReload} />

      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search leads..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
        </div>
        <select
          value={filterStage}
          onChange={(e) => setFilterStage(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none bg-white"
        >
          <option value="all">All Stages</option>
          {PIPELINE_STAGES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </div>

      {filtered.length > 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden divide-y divide-gray-50">
          {filtered.map((lead) => (
            <LeadRow key={lead.id} lead={lead} onClick={() => onLeadClick(lead)} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">{search || filterStage !== "all" ? "No leads match your filter." : "No leads yet."}</p>
        </div>
      )}
    </div>
  );
}

// ─── Main Shell ───────────────────────────────────────────────────────────────

function GrowthHubInner() {
  const { leads, metrics, settings, saveSettings, loading, selectedLead, setSelectedLead, reload } = useGrowth();
  const [view, setView] = useState("overview");
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showScraper, setShowScraper] = useState(false);

  // Settings state
  const [signupUrl, setSignupUrl] = useState(settings?.signupUrl || "https://pebbletheapp.com/signup");
  const [demoUrl, setDemoUrl] = useState(settings?.demoBookingUrl || "");
  const [reportEmail, setReportEmail] = useState(settings?.reportEmail || "");
  const [reportPhone, setReportPhone] = useState(settings?.reportPhone || "");
  const [enabledReports, setEnabledReports] = useState(
    settings?.enabledReports || ["daily", "weekly", "monthly", "quarterly", "annual"]
  );
  // Voice samples — real writing Aaron pastes in so the agent sounds like him
  const [voiceSamples, setVoiceSamples] = useState(
    settings?.voiceSamples || ["", "", ""]
  );
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [sendingReport, setSendingReport] = useState(null);
  const [reportSent, setReportSent] = useState(null);

  // Sync settings fields when settings load from Firestore
  useEffect(() => {
    if (!settings || Object.keys(settings).length === 0) return;
    if (settings.signupUrl) setSignupUrl(settings.signupUrl);
    if (settings.demoBookingUrl !== undefined) setDemoUrl(settings.demoBookingUrl);
    if (settings.reportEmail) setReportEmail(settings.reportEmail);
    if (settings.reportPhone) setReportPhone(settings.reportPhone);
    if (settings.enabledReports) setEnabledReports(settings.enabledReports);
    if (settings.voiceSamples) setVoiceSamples(settings.voiceSamples.concat(["","",""]).slice(0,3));
  }, [settings]);

  const REPORT_CADENCES = [
    { id: "daily",     label: "Daily",     sub: "Every day at 8am ET" },
    { id: "weekly",    label: "Weekly",    sub: "Every Monday at 8am ET" },
    { id: "monthly",   label: "Monthly",   sub: "1st of each month" },
    { id: "quarterly", label: "Quarterly", sub: "Jan, Apr, Jul, Oct 1st" },
    { id: "annual",    label: "Annual",    sub: "January 1st" },
  ];

  const toggleReport = (id) => {
    setEnabledReports((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    await saveSettings({ signupUrl, demoBookingUrl: demoUrl, reportEmail, reportPhone, enabledReports, voiceSamples: voiceSamples.filter((s) => s.trim()) });
    setSavingSettings(false);
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2500);
  };

  const handleSendNow = async (period) => {
    setSendingReport(period);
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const call = httpsCallable(getFunctions(), "sendGrowthReportNow");
      await call({ period });
      setReportSent(period);
      setTimeout(() => setReportSent(null), 3000);
    } catch (err) {
      console.error("Report send error:", err);
    } finally {
      setSendingReport(null);
    }
  };

  const VIEWS = [
    { id: "overview", label: "Overview" },
    { id: "leads", label: `Leads${leads.length ? ` (${leads.length})` : ""}` },
    { id: "pipeline", label: "Pipeline" },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-gray-700" />
              <h1 className="text-lg font-semibold text-gray-900">Growth</h1>
              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full font-medium">AI</span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">Aaron Jordan's AI growth team</p>
          </div>
          {view !== "settings" && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowScraper(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition"
              >
                <Zap className="w-4 h-4" />
                Scrape Leads
              </button>
              <button
                onClick={() => setShowImport(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition"
              >
                <Upload className="w-4 h-4" />
                Import CSV
              </button>
              <button
                onClick={() => setShowAdd(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition"
              >
                <Plus className="w-4 h-4" />
                Add Lead
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={`px-4 py-1.5 text-sm rounded-lg font-medium transition ${
                view === v.id
                  ? "bg-gray-900 text-white"
                  : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-w-5xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : (
          <>
            {view === "overview" && (
              <OverviewView leads={leads} metrics={metrics} onLeadClick={setSelectedLead} onReload={reload} />
            )}
            {view === "leads" && (
              <LeadsView leads={leads} onLeadClick={setSelectedLead} onReload={reload} />
            )}
            {view === "pipeline" && (
              <PipelineView leads={leads} onLeadClick={setSelectedLead} />
            )}
            {view === "settings" && (
              <div className="max-w-lg space-y-6">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 mb-1">Growth Settings</h2>
                  <p className="text-sm text-gray-400">Configure outreach defaults and automated reports.</p>
                </div>

                {/* Outreach */}
                <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Outreach CTAs</p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                      <Link className="w-4 h-4 text-gray-400" />
                      Signup URL <span className="text-xs text-gray-400 font-normal ml-1">— primary CTA</span>
                    </label>
                    <input
                      type="url"
                      value={signupUrl}
                      onChange={(e) => setSignupUrl(e.target.value)}
                      placeholder="https://pebbletheapp.com/signup"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                    <p className="text-xs text-gray-400 mt-1.5">Agent sends every organizer here first. Basic plan is free — no friction, no call needed.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                      <Link className="w-4 h-4 text-gray-400" />
                      Demo Booking URL <span className="text-xs text-gray-400 font-normal ml-1">— secondary, only if they ask</span>
                    </label>
                    <input
                      type="url"
                      value={demoUrl}
                      onChange={(e) => setDemoUrl(e.target.value)}
                      placeholder="https://calendly.com/aaronjordan/pebble-demo"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                    <p className="text-xs text-gray-400 mt-1.5">Mentioned softly only when it fits. Never the main ask.</p>
                  </div>
                </div>

                {/* Voice Samples */}
                <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Your Voice</p>
                    <p className="text-xs text-gray-400 mt-1">Paste 1–3 real emails or DMs you've actually written. The agent reads these before every message it generates — the more real writing you give it, the more it sounds like you.</p>
                  </div>
                  {voiceSamples.map((sample, i) => (
                    <div key={i}>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Sample {i + 1}</label>
                      <textarea
                        value={sample}
                        onChange={(e) => {
                          const updated = [...voiceSamples];
                          updated[i] = e.target.value;
                          setVoiceSamples(updated);
                        }}
                        rows={4}
                        placeholder={i === 0 ? `e.g. "Hey Sarah — stumbled on the Peach Festival and loved what y'all are building. Curious, how are you handling vendor sign-ups right now? I built something called Pebble for exactly this — setup takes 2 minutes, basic plan is free. No pressure but figured it might be useful: pebbletheapp.com/signup\n\n— Aaron"` : `Another email or DM you've sent...`}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none font-mono text-xs leading-relaxed"
                      />
                    </div>
                  ))}
                  <p className="text-xs text-gray-400 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3 text-blue-400" />
                    Winning messages (leads who replied) are automatically saved and used too — the agent gets smarter over time.
                  </p>
                </div>

                {/* Reports */}
                <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Automated Reports</p>
                  <p className="text-xs text-gray-400 -mt-2">Your AI growth team sends you a report via email and text — pipeline stats, response rate, top leads, and what to do next.</p>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Report Email</label>
                    <input
                      type="email"
                      value={reportEmail}
                      onChange={(e) => setReportEmail(e.target.value)}
                      placeholder="aaron@usepebble.io"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Report Phone (SMS)</label>
                    <input
                      type="tel"
                      value={reportPhone}
                      onChange={(e) => setReportPhone(e.target.value)}
                      placeholder="+15025551234"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                    <p className="text-xs text-gray-400 mt-1">E.164 format, e.g. +15025551234</p>
                  </div>

                  {/* Cadence toggles */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Report Cadence</p>
                    <div className="space-y-2">
                      {REPORT_CADENCES.map((c) => {
                        const enabled = enabledReports.includes(c.id);
                        const sending = sendingReport === c.id;
                        const sent = reportSent === c.id;
                        return (
                          <div key={c.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => toggleReport(c.id)}
                                className={`w-9 h-5 rounded-full transition relative ${enabled ? "bg-gray-900" : "bg-gray-200"}`}
                              >
                                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${enabled ? "left-4" : "left-0.5"}`} />
                              </button>
                              <div>
                                <p className="text-sm font-medium text-gray-800">{c.label}</p>
                                <p className="text-xs text-gray-400">{c.sub}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleSendNow(c.id)}
                              disabled={sending || !reportEmail}
                              className="text-xs text-gray-400 hover:text-gray-700 border border-gray-200 rounded-lg px-2.5 py-1 transition disabled:opacity-40"
                            >
                              {sent ? "✓ Sent" : sending ? "Sending..." : "Send now"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition"
                >
                  {settingsSaved ? (
                    <><Check className="w-4 h-4" /> Saved</>
                  ) : (
                    <><Save className="w-4 h-4" /> {savingSettings ? "Saving..." : "Save Settings"}</>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lead detail slide-in */}
      {selectedLead && (
        <LeadDetailPanel
          lead={leads.find((l) => l.id === selectedLead.id) || selectedLead}
          onClose={() => setSelectedLead(null)}
        />
      )}

      {showAdd && <AddLeadModal onClose={() => setShowAdd(false)} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} />}
      {showScraper && <ScraperPanel onClose={() => { setShowScraper(false); reload(); }} />}
    </div>
  );
}

export default function GrowthHub() {
  return (
    <GrowthProvider>
      <GrowthHubInner />
    </GrowthProvider>
  );
}
